<script setup lang="ts">
import api from "~/utils/auth/api";
import { useSettings } from "~/store/settings";
import { useQuery } from "@tanstack/vue-query";
import RacingEvent from "~/components/layout/TrackRacing/Shared/Event/RacingEvent.vue";
import { IEvent } from "~/types/sports/event";
import DailyRacesLoading from "./DailyRacesLoading.vue";
import ResultsIcon from "../../Shared/ResultsIcon.vue";
import Accordion from "./Accordion.vue";
import t from "~/utils/translate";
import groupBy from "lodash/groupBy";

const settings = useSettings();
const router = useRouter();
const route = useRoute();
export interface IDailyLeagues {
	name: string;
	sportEvents: IEvent[];
}
export interface IDailyRegions {
	regions: IDailyLeagues[];
}

export interface ILocalDailyRegions {
	id: string;
	name: string;
	leagues: {
		[key: string]: IEvent[];
	};
}
interface Props {
	selectedPeriod?: string | undefined;
	sportId: string | undefined;
	isSpecialSelection?: boolean | undefined;
}

const props = defineProps<Props>();
const selectedDay = computed(() => {
	return props.selectedPeriod === "today" ? "Today" : "Tomorrow";
});

const eventType = computed(() => {
	return route?.query?.eventType;
});

const selectedEventId = computed(() => {
	return route?.query?.eventId;
});

const dailyRacesApiCall = () => {
	return api<IDailyRegions>({
		url: `/TrackRacing/GetDaily?sportId=${props.sportId}&period=${selectedDay.value}&isVirtual=false&countryCode=${settings.config?.appSetting?.CountryCode}&timeZoneOffset=2`,
		type: "horses",
		apiVersion: "v1",
		options: { method: "GET" },
	}).then(async (response) => {
		const regionsWithEvents = response.regions.map((r) => ({
			id: r.name.toLowerCase().replaceAll(" ", "-"),
			name: r.name,
			leagues: groupBy(r.sportEvents, "league"),
		}));
		return regionsWithEvents;
	});
};

const scrollToEvent = (containers: { league: string; shouldScroll: boolean; activeEvent: string }[]) => {
	containers.forEach((container) => {
		const item = document.getElementById(`${container.activeEvent}`);
		if (item && container.shouldScroll) {
			const parent = item.closest(".league-row") as HTMLElement;
			if (parent) {
				parent.scrollLeft = item.offsetLeft - parent.offsetLeft - 40;
			}
		}
	});
};

const handleSpecialEvent = (event: ILocalDailyRegions) => {
	const activeEvent = event.leagues[eventType.value as string].find((event) => event.isActive);
	const specialEvent = activeEvent?.eventId ? activeEvent : event.leagues[eventType.value as string][0];
	router.push({ query: { ...route.query, regionId: specialEvent.regionId, league: specialEvent.league, eventId: specialEvent.eventId } });
};

const {
	data: dailyRaceEvents,
	isLoading,
	isFetching,
	refetch: refetchDailyRaces,
} = useQuery<ILocalDailyRegions[]>({
	queryKey: ["GetDailyRaces", props.sportId, selectedDay.value],
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	queryFn: () => dailyRacesApiCall(),
	onSuccess: (data) => {
		if (props.isSpecialSelection && data.length) {
			handleSpecialEvent(data[0]);
		}
		const container: { league: string; shouldScroll: boolean; activeEvent: string }[] = [];
		data.forEach((r) => {
			Object.keys(r.leagues).forEach((league) => {
				container.push({
					league,
					shouldScroll: r.leagues[league].some(({ isActive }) => !isActive),
					activeEvent: `${r.leagues[league].find(({ isActive }) => isActive)?.eventId}`,
				});
			});
		});
		setTimeout(() => {
			scrollToEvent(container);
		}, 500);
	},
});

const dailyEventsWithSpecialEvents = computed(() => {
	const specialDailyEvents = dailyRaceEvents.value?.filter((e) => e.leagues[eventType.value as string]).map((region) => ({ ...region }));
	return props.isSpecialSelection ? specialDailyEvents : dailyRaceEvents.value;
});

const getTimeFromEpoch = (epoch: number) => {
	const date = new Date(epoch * 1000);
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	return `${hours}:${minutes}`;
};
watch(eventType, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		refetchDailyRaces();
	}
});
</script>
<template>
	<div>
		<RacingEvent
			v-if="selectedEventId && dailyEventsWithSpecialEvents && dailyEventsWithSpecialEvents.length > 0"
			:selected-event-id="+selectedEventId"
			:dailyRaceEvents="dailyEventsWithSpecialEvents"
			:show-related-events-nav="true"
			@handle-auto-scroll="refetchDailyRaces()"
		/>
		<div v-else>
			<DailyRacesLoading v-if="isFetching || isLoading" />
			<template v-else>
				<div v-if="dailyEventsWithSpecialEvents && dailyEventsWithSpecialEvents.length > 0">
					<Accordion v-for="region in dailyEventsWithSpecialEvents" :title="region.name" :region-id="region.id" :default-open="true">
						<template #content>
							<div class="overflow-x-auto" v-for="(events, key) in region.leagues" :key="key">
								<div class="flex items-center h-[32px] text-xs font-bold bg-light-50 dark:bg-dark-700">
									<div class="my-auto mx-2">
										{{ key }}
									</div>
								</div>

								<div class="flex overflow-x-auto league-row">
									<div class="flex flex-nowrap items-center p-1">
										<div
											v-for="event in events"
											class="flex justify-center items-center mx-1 w-[90px] h-[42px] rounded-md cursor-pointer font-bold bg-light-50 dark:bg-dark-700 text-dark-800 dark:text-light-50"
											@click="router.push({ query: { ...route.query, regionId: region.id, league: event.league, eventId: event.eventId } })"
											:id="`${event.eventId}`"
										>
											<div class="text-xs mr-2">{{ getTimeFromEpoch(event.venueEpoch) }}</div>

											<ResultsIcon v-if="!event.isActive" />
										</div>
									</div>
								</div>
							</div>
						</template>
					</Accordion>
				</div>
				<div v-else class="w-full rounded-lg bg-light-50 dark:bg-dark-800 text-xs text-center font-bold my-2 py-2">
					{{ t("no-events-available") }}
				</div>
			</template>
		</div>
	</div>
</template>
